import { useMemo } from "react";
import { CountrySelect } from "@/components/form/CountrySelect";
import {
  getNationalForDisplay,
  getPhonePlaceholder,
  parsePrefilledPhone,
  sanitizeNationalInput,
} from "@/lib/phoneNumber";
import { cn } from "@/lib/utils";

export interface PhoneNumberInputProps {
  phoneCountry: string;
  phoneNational: string;
  onPhoneCountryChange: (value: string) => void;
  onPhoneNationalChange: (value: string) => void;
  phoneError?: string;
  phoneCountryError?: string;
  placeholder?: string;
  dialCodeClassName?: string;
  nationalClassName?: string;
  className?: string;
}

export function PhoneNumberInput({
  phoneCountry,
  phoneNational,
  onPhoneCountryChange,
  onPhoneNationalChange,
  phoneError,
  phoneCountryError,
  placeholder,
  dialCodeClassName,
  nationalClassName,
  className,
}: PhoneNumberInputProps) {
  const displayNational = useMemo(
    () => getNationalForDisplay(phoneNational, phoneCountry || "US"),
    [phoneNational, phoneCountry],
  );

  const phonePlaceholder =
    placeholder ?? getPhonePlaceholder(phoneCountry) ?? "555-123-4567";

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!pasted?.trim()) return;

    const parsed = parsePrefilledPhone(pasted, phoneCountry || "US");
    if (!parsed) return;

    e.preventDefault();
    onPhoneCountryChange(parsed.country);
    onPhoneNationalChange(parsed.national);
  };

  const dialError = phoneCountryError;
  const nationalError = phoneError;

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <div className="w-[104px] flex-shrink-0">
        <CountrySelect
          mode="dialCode"
          value={phoneCountry}
          onValueChange={onPhoneCountryChange}
          placeholder="Code"
          buttonClassName={cn(
            "h-12 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50/50 focus:border-[#166B93]",
            dialError && "border-red-500 focus:border-red-500",
            dialCodeClassName,
          )}
          aria-invalid={!!dialError}
        />
        {dialError && (
          <p className="text-red-500 text-xs mt-1.5 font-medium">{dialError}</p>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={15}
          value={displayNational}
          onChange={(e) => {
            onPhoneNationalChange(sanitizeNationalInput(e.target.value));
          }}
          onPaste={handlePhonePaste}
          placeholder={phonePlaceholder}
          autoComplete="tel-national"
          aria-invalid={!!nationalError}
          className={cn(
            "w-full h-12 text-base rounded-xl border-2 bg-gray-50/50 px-4 focus:bg-white focus:ring-4 focus:ring-[#166B93]/10 hover:border-gray-300 transition-all duration-300 border-gray-200 focus:border-[#166B93]",
            nationalError && "border-red-500 focus:border-red-500",
            nationalClassName,
          )}
        />
        {nationalError && (
          <p className="text-red-500 text-xs mt-1.5 font-medium">{nationalError}</p>
        )}
      </div>
    </div>
  );
}
